import * as React from 'react';

import moment from 'moment';
import axios from 'axios';

import { Alert, Button, Modal } from '@shared/components/bootstrap';
import { Currency } from '@shared/components/helpers';

interface IPreview {
  lastPayment: { total: number | string; on: string };
  nextPayment: {
    subtotal: number | string;
    proration: number | string;
    total: number | string;
    on: string;
  };
  futurePayment: { total: number | string; on: string; day_of_month: number };
}

const dateSuffix = (input: string | Date): string => {
  const day = moment(input).date();
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const relevantDigits = day < 30 ? day % 20 : day % 30;
  const suffix = relevantDigits <= 3 ? suffixes[relevantDigits] : suffixes[0];
  return suffix;
};

export const BillingDateChangeModal: React.FC<{
  accountID: string;
  billingDateChangedOn: string | null;
  subscriptionBillingDay: number;
  dayOptions: number[];
  onClose(): void;
  onSuccess(): void;
}> = ({ accountID, billingDateChangedOn, subscriptionBillingDay, dayOptions, onClose, onSuccess }) => {
  const [dayOfMonth, setDayOfMonth] = React.useState<number | ''>('');
  const [preview, setPreview] = React.useState<IPreview | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [errors, setErrors] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (!dayOfMonth) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setErrors(undefined);
    void axios
      .get<IPreview>(`/accounts/${accountID}/billing_date.json`, {
        params: { day_of_month: dayOfMonth },
      })
      .then(({ data }) => {
        if (!cancelled) setPreview(data);
      })
      .catch((e: unknown) => {
        const err = e as { response?: { data?: { message?: string } } };
        if (!cancelled) {
          setErrors(err.response?.data?.message ?? 'Unable to load preview.');
          setPreview(null);
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountID, dayOfMonth]);

  const submit = async () => {
    if (!dayOfMonth) return;
    setSubmitting(true);
    setErrors(undefined);
    try {
      await axios.post(`/accounts/${accountID}/billing_date.json`, { day_of_month: dayOfMonth });
      onSuccess();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setErrors(err.response?.data?.message ?? 'Update failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal centered onClose={onClose}>
      <Modal.Content>
        <div className="dialog billing-date-change-modal">
          {previewLoading && <div className="spinner">Loading</div>}
          <div className="dialog-contents">
            {errors && (
              <Alert style="danger">
                <strong>Error:</strong> {errors}
              </Alert>
            )}
            <p className="dialog-title">Billing Date Change</p>
            {billingDateChangedOn && (
              <p className="dialog-description">
                Billing date last changed on <strong>{moment(billingDateChangedOn).format('MMM D, YYYY')}</strong>
              </p>
            )}
            <hr />
            <div className="form-inline dialog-description">
              <span>Bill this customer on the </span>
              <div className="input-group">
                <select
                  className="custom-select mb-2 mr-sm-2 mb-sm-0"
                  value={dayOfMonth === '' ? '' : String(dayOfMonth)}
                  onChange={(e) => setDayOfMonth(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">—</option>
                  {dayOptions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <span> day of the month (current billing day {subscriptionBillingDay} is excluded).</span>
            </div>
          </div>
          {preview && (
            <div className="row text-center">
              <div className="col-xs-4">
                <div className="panel panel-danger">
                  <div className="panel-heading">
                    <h3 className="panel-title">Last Payment</h3>
                  </div>
                  <div className="panel-body">
                    <h1>
                      <Currency value={preview.lastPayment.total} />
                    </h1>
                  </div>
                  <div className="panel-footer">
                    <div className="vertical-center">on {moment(preview.lastPayment.on).format('MMM D, YYYY')}</div>
                  </div>
                </div>
              </div>
              <div className="col-xs-4">
                <div className="panel panel-warning">
                  <div className="panel-heading">
                    <h3 className="panel-title">Next Payment</h3>
                  </div>
                  <div className="panel-body">
                    <table>
                      <tbody>
                        <tr>
                          <td className="line-item">Existing sub</td>
                          <td className="amount">
                            <Currency value={preview.nextPayment.subtotal} />
                          </td>
                        </tr>
                        <tr>
                          <td className="line-item">Proration</td>
                          <td className="amount">
                            <Currency value={preview.nextPayment.proration} />
                          </td>
                        </tr>
                        <tr>
                          <td className="line-item line-item-total">Total</td>
                          <td className="amount amount-total">
                            <Currency value={preview.nextPayment.total} />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="panel-footer vertical-center">
                    <div className="vertical-center text-danger">
                      <strong>on {moment(preview.nextPayment.on).format('MMM D, YYYY')}</strong>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xs-4">
                <div className="panel panel-success">
                  <div className="panel-heading">
                    <h3 className="panel-title">Future Payments</h3>
                  </div>
                  <div className="panel-body">
                    <h1>
                      <Currency value={preview.futurePayment.total} />
                    </h1>
                  </div>
                  <div className="panel-footer">
                    <div className="vertical-center">
                      on the {preview.futurePayment.day_of_month}
                      {dateSuffix(preview.futurePayment.on)} of every month
                      <br />
                      starting {moment(preview.futurePayment.on).format('MMM, YYYY')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="dialog-actions">
            <Button kind="danger" className="btn-lg" onClick={onClose}>
              Cancel
            </Button>
            <Button
              kind="primary"
              className="btn-lg"
              disabled={!dayOfMonth || previewLoading || submitting}
              onClick={() => void submit()}
            >
              Change Billing Date
            </Button>
          </div>
          <hr />
          <p className="dialog-help">
            <a
              href="https://drive.google.com/file/d/0B9NjJFAJxcU-MVVLdWEtd1MySVU/view"
              target="_blank"
              rel="noreferrer"
            >
              Click here
            </a>{' '}
            for help changing a customer&apos;s billing date.
          </p>
        </div>
      </Modal.Content>
    </Modal>
  );
};
